import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import Strings from '../../../../i18n';
import { IEffectItemStore } from '../../../../types/effects/effectsStoreInterface';
import { SelectItemMethod } from '../../../../components/commons/SideBarComponents/SelectItemMethod/selectItemMethod';
import { useGetGroupsByLocationIdMutation } from '../../../../services/groups/groupsService';
import { useGetLightsZonesByLocationMutation } from '../../../../services/lightsZones/lightsZonesService';
import { LoadingRowsComponent } from '../../../../components/commons/LoadingRows/LoadingRows';
import {
  CommandsTypesItemEnum,
  LightCapabilitiesEnum
} from '../../../../constants/commonEnums';
import { useExecuteEffectMutation } from '../../../../services/effects/effectsService';
import { colors } from '../../../../constants/colors';
import {
  ButtonStyled,
  ButtonStyledCancel,
  InputStyled,
  noBorderStyle
} from '../../../device/addDeviceToLocation/addDeviceToLocationStyles';
import { LocationUserSelected } from '../../../../types/location/location';
import { ItemLocationSelected } from '../../../../components/itemLocationSelected/itemLocationSelected';
import { useGetUserSelectedLocationMutation } from '../../../../services/device/deviceService';
import { selectCurrentUser } from '../../../../store/slices/login/login';

interface ITestEffectSideBarProps {
  closeSideBar: () => void;
  effect: IEffectItemStore;
}
export const TestEffectSideBar = ({
  closeSideBar,
  effect
}: ITestEffectSideBarProps) => {
  const contextUser = useSelector(selectCurrentUser);
  const [
    getLocations,
    {
      isSuccess: isSuccessLocations,
      data: dataLocations,
      isLoading: isLoadingLocations
    }
  ] = useGetUserSelectedLocationMutation();

  const [filterText, setFilterText] = useState('');

  const [typingTimeout, setTypingTimeout] = useState<any | null>(null);
  const [
    getGroupsByLocationId,
    { data: dataGroups, isSuccess: isSuccessGroups, isLoading: isLoadingGroups }
  ] = useGetGroupsByLocationIdMutation();

  const [
    getLightsZonesByLocation,
    {
      data: dataLightsZones,
      isSuccess: isSuccessLightsZones,
      isLoading: isLoadingLightsZones
    }
  ] = useGetLightsZonesByLocationMutation();

  const [locationId, setLocationId] = useState<string | number | undefined>(
    undefined
  );

  const [selectedGroup, setSelectedGroup] = useState<
    string | number | undefined
  >(undefined);

  const [selectedLightZone, setSelectedLightZone] = useState<
    string | number | undefined
  >(undefined);

  const [target, setTarget] = useState<
    { id: number; type: string } | undefined
  >(undefined);

  const [locationsPreferred, setLocationsPreferred] = useState<
    LocationUserSelected[]
  >([]);

  const [preLimit, setPreLimit] = useState(0);
  const [beforeLimit, setBeforeLimit] = useState(5);

  const resetValues = () => {
    setSelectedGroup(undefined);
    setSelectedLightZone(undefined);
    setTarget(undefined);
  };

  const [
    executeEffect,
    { isSuccess: isSuccessExecuteEffect, isLoading: isLoadingExecuteEffect }
  ] = useExecuteEffectMutation();

  useEffect(() => {
    getLocations({
      userId: contextUser.userId?.toString() || '',
      searchText: filterText
    });
  }, []);

  useEffect(() => {
    if (isSuccessLocations && dataLocations) {
      setLocationsPreferred(dataLocations.filter((loc) => loc.order !== 0));
    }
  }, [isSuccessLocations]);

  const handleChangeLightZone = (e: { target: { value: string } }) => {
    setSelectedLightZone(e.target.value);
    setTarget({
      id: Number(e.target.value),
      type: CommandsTypesItemEnum.Light
    });
  };

  const handleChangeLocation = (e: { target: { value: string } }) => {
    setLocationId(e.target.value);
  };
  useEffect(() => {
    if (locationId) {
      resetValues();
      getGroupsByLocationId(Number(locationId));
      getLightsZonesByLocation(Number(locationId));
    }
  }, [locationId]);

  const handleChangeGroup = (e: { target: { value: string } }) => {
    setSelectedGroup(e.target.value);
    setTarget({
      id: Number(e.target.value),
      type: CommandsTypesItemEnum.Group
    });
  };

  const handleTestEffect = () => {
    if (target) {
      executeEffect({
        id: target.id,
        type: target.type,
        effectId: effect?.id
      });
    }
  };

  useEffect(() => {
    if (isSuccessExecuteEffect) {
      toast.success(Strings.effects.effectExecutedSuccessfully);
      resetValues();
      closeSideBar();
    }
  }, [isSuccessExecuteEffect]);

  const items = filterText.length ? dataLocations : locationsPreferred;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && filterText !== undefined) {
      getLocations({
        userId: contextUser.userId?.toString() || '',
        searchText: filterText.length ? filterText : ''
      });
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [filterText]);

  const handleChange = (value: string) => {
    setFilterText(value);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    if (value.length > 2) {
      setTypingTimeout(
        setTimeout(() => {
          getLocations({
            userId: contextUser.userId?.toString() || '',
            searchText: value
          });
        }, 1700)
      );
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  return (
    <>
      <StyledContainerItems>
        <StyledTitleSideBar>
          {Strings.effects.testEffect} {effect?.name}
        </StyledTitleSideBar>
        {isSuccessLocations && (
          <Grid direction="row" container>
            <Typography
              fontSize={13}
              color={colors.darkGray}
              marginTop={1}
              marginLeft={2}
            >
              {Strings.locations.searchByNameOwnerAddress}
            </Typography>
            <Grid
              item
              xs={12}
              bgcolor={colors.lightGray}
              padding={1}
              borderRadius={2}
            >
              <InputStyled
                sx={noBorderStyle}
                fullWidth
                onChange={(e) => handleChange(e.target.value)}
              />

              {items &&
                items.slice(preLimit, beforeLimit).map((loc) => (
                  <ItemLocationSelected
                    devices={loc.devicesCount}
                    selected={loc.id === locationId}
                    action={() => {
                      setLocationId(loc.id);
                    }}
                    locationName={loc.name}
                    ownerName={loc.owner}
                  />
                ))}
            </Grid>
            <Grid
              item
              container
              justifyContent="flex-end"
              marginTop={1}
              marginBottom={1}
            >
              <ButtonStyledCancel
                disabled={preLimit === 0 && beforeLimit === 5}
                variant="contained"
                size="small"
                onClick={() => {
                  setPreLimit((prev) => prev - 5);
                  setBeforeLimit((prev) => prev - 5);
                }}
              >
                {Strings.common.previous}
              </ButtonStyledCancel>
              <ButtonStyled
                disabled={items && items?.length <= beforeLimit}
                onClick={() => {
                  setPreLimit((prev) => prev + 5);
                  setBeforeLimit((prev) => prev + 5);
                }}
                variant="contained"
                loading={isLoadingLocations}
                size="small"
              >
                {Strings.common.next}
              </ButtonStyled>
            </Grid>
          </Grid>
        )}
        {isSuccessGroups && (
          <SelectItemMethod
            showClear={Boolean(selectedGroup)}
            clearAction={() => {
              setSelectedGroup(undefined);
              setTarget(undefined);
            }}
            item={{
              disabled: Boolean(selectedLightZone),
              label: Strings.common.selectAGroup,
              currentValue: selectedGroup,
              options:
                dataGroups
                  ?.filter((group) =>
                    group.capabilities?.some(
                      (capability) =>
                        capability.name === LightCapabilitiesEnum.TRIM_LIGHT
                    )
                  )
                  .map((group) => ({
                    id: group.groupId,
                    name: group.groupName
                  })) || [],
              onChange: handleChangeGroup,
              placeholder: Strings.devices.productType
            }}
          />
        )}
        {isSuccessLightsZones && (
          <SelectItemMethod
            showClear={Boolean(selectedLightZone)}
            clearAction={() => {
              setSelectedLightZone(undefined);
              setTarget(undefined);
            }}
            item={{
              disabled: Boolean(selectedGroup),
              label: Strings.common.selectALight,
              currentValue: selectedLightZone,
              options:
                dataLightsZones
                  ?.filter(
                    (lightZone) =>
                      lightZone.typeName === Strings.trimLights.trimLight
                  )
                  .map((lightZone) => ({
                    id: lightZone.id,
                    name: lightZone.name
                  })) || [],
              onChange: handleChangeLightZone,
              placeholder: Strings.devices.productType
            }}
          />
        )}
        {isLoadingLocations || isLoadingGroups || isLoadingLightsZones ? (
          <LoadingRowsComponent />
        ) : null}
      </StyledContainerItems>
      <StyledContainerFooterSideBar>
        <button
          type="button"
          className="btn-cancel"
          onClick={() => {
            closeSideBar();
          }}
        >
          {Strings.common.cancel}
        </button>
        <button
          type="button"
          className="btn-save"
          disabled={!target}
          onClick={() => {
            handleTestEffect();
          }}
        >
          {Strings.common.send}
        </button>
      </StyledContainerFooterSideBar>
    </>
  );
};
